/**
 * ModuleManagementScreen — Module → SubModule → Screen hierarchy
 * Features: Lazy tree, right-click context menu, Edit modal, Move-To modal, Grid context menu
 * State: React Context (ModuleTreeContext)
 * Data: 100% from API — /api/uams/modules, submodules, screens
 */
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { ChevronRight, ChevronDown, Loader, Search, RotateCcw, Filter, Monitor, Layers, Grid3X3, Activity, Edit3, Move, Trash2, X, Check, ArrowRight, MoreVertical } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { ModuleTreeProvider, useModuleTree } from '../../context/ModuleTreeContext';
import { erpToast } from '../../components/erp/ERPToast';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { HelpInfo } from '../../components/help-engine/HelpInfo';
import '../../styles/erp-theme.css';

/* ═══════════════════════════════════════════════════
   CONTEXT MENU (portal-free, absolute positioned)
═══════════════════════════════════════════════════ */
const ContextMenu = ({ x, y, items, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Clamp position so menu doesn't overflow viewport
  const menuStyle = {
    position: 'fixed', top: Math.min(y, window.innerHeight - 200), left: Math.min(x, window.innerWidth - 200),
    zIndex: 1300, minWidth: 180, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    boxShadow: '0 12px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)', padding: '4px 0',
    animation: 'erp-dropdown-in 120ms ease-out',
  };

  return (
    <div ref={ref} style={menuStyle} data-testid="context-menu">
      {items.map((item, i) => item.divider ? (
        <div key={i} style={{ height: 1, background: '#f1f5f9', margin: '4px 8px' }} />
      ) : (
        <button key={i} onClick={() => { item.action(); onClose(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 14px', fontSize: 12, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: item.danger ? '#dc2626' : '#1e293b', transition: 'background 0.1s, color 0.1s', borderRadius: 0 }}
          onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = item.danger ? '#fef2f2' : '#f1f5f9'; e.currentTarget.style.color = item.danger ? '#b91c1c' : '#0f172a'; }}
          onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'none'; e.currentTarget.style.color = item.danger ? '#dc2626' : '#1e293b'; }}
          data-testid={`ctx-${item.id}`}>
          <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.danger ? '#ef4444' : '#64748b' }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   EDIT MODAL
═══════════════════════════════════════════════════ */
const EditModal = ({ node, onClose, onSave }) => {
  const [name, setName] = useState(node?.name || '');
  const [screenType, setScreenType] = useState(node?.raw?.ScreenType || '');
  const [saving, setSaving] = useState(false);
  const { screenTypes } = useModuleTree();

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const ok = await onSave(node, { name: name.trim(), screenType: screenType.trim() });
    setSaving(false);
    if (ok) { erpToast.success(`${node.type} updated`); onClose(); }
    else erpToast.error('Update failed');
  };

  return (
    <div className="erp-modal-overlay" data-testid="edit-modal">
      <div style={{ background: '#fff', borderRadius: 8, width: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Edit {node?.type}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Name<HelpInfo controlId="Name" controlName="Name" /></label>
            <input value={name} onChange={e => setName(e.target.value)} className="erp-input" style={{ fontSize: 12 }} autoFocus data-testid="edit-name" data-help-control="Name" />
          </div>
          {node?.type === 'screen' && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Screen Type<HelpInfo controlId="ScreenType" controlName="Screen Type" /></label>
              <select value={screenType} onChange={e => setScreenType(e.target.value)} className="erp-input erp-select" style={{ fontSize: 12 }} data-testid="edit-type" data-help-control="ScreenType">
                <option value="">--Select--</option>
                {screenTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={onClose} className="erp-btn erp-btn-ghost"><X size={12} /> Cancel</button>
            <button onClick={handleSave} disabled={saving} className="erp-btn erp-btn-primary"><Check size={12} /> {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MOVE-TO MODAL — supports move, promote, demote
═══════════════════════════════════════════════════ */
const MoveModal = ({ node, onClose }) => {
  const { modules, submodules, moveNode, promoteToModule, demoteToSubModule } = useModuleTree();
  const [targetId, setTargetId] = useState('');
  const [afterModuleId, setAfterModuleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(node?.type === 'module' ? 'demote' : 'move');

  const isModule = node?.type === 'module';
  const isSubmodule = node?.type === 'submodule';
  const isScreen = node?.type === 'screen';

  const modes = [];
  if (isSubmodule) {
    modes.push({ id: 'move', label: 'Move to another Module' });
    modes.push({ id: 'promote', label: 'Promote to Module' });
  }
  if (isModule) {
    modes.push({ id: 'demote', label: 'Demote to SubModule' });
  }
  if (isScreen) {
    modes.push({ id: 'move', label: 'Move to another SubModule' });
  }

  const targets = useMemo(() => {
    if (mode === 'move' && isSubmodule) return modules.map(m => ({ id: m.ModuleId, name: m.ModuleName }));
    if (mode === 'move' && isScreen) return submodules.map(s => ({ id: s.SubModuleId, name: s.SubModuleName }));
    if (mode === 'demote') return modules.filter(m => m.ModuleId !== node?.raw?.ModuleId).map(m => ({ id: m.ModuleId, name: m.ModuleName }));
    return [];
  }, [mode, isSubmodule, isScreen, modules, submodules, node]);

  // Sorted modules for "Insert After" picker
  const sortedModules = useMemo(() => [...modules].sort((a, b) => a.ModuleId - b.ModuleId), [modules]);

  const needsTarget = mode === 'move' || mode === 'demote';

  const handleExecute = async () => {
    if (needsTarget && !targetId) return;
    setSaving(true);
    let ok = false;
    if (mode === 'promote') {
      ok = await promoteToModule(node, afterModuleId ? parseInt(afterModuleId) : undefined);
    } else if (mode === 'demote') {
      ok = await demoteToSubModule(node, parseInt(targetId));
    } else {
      ok = await moveNode(node, isSubmodule ? parseInt(targetId) : targetId);
    }
    setSaving(false);
    if (ok) { erpToast.success(mode === 'promote' ? 'Promoted to Module' : mode === 'demote' ? 'Demoted to SubModule' : 'Moved successfully'); onClose(); }
    else erpToast.error('Operation failed');
  };

  return (
    <div className="erp-modal-overlay" data-testid="move-modal">
      <div style={{ background: '#fff', borderRadius: 8, width: 480, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Move / Remap: {node?.name}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 16 }}>
          {/* Mode selector */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 6 }}>Operation</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {modes.map(m => (
                <button key={m.id} onClick={() => { setMode(m.id); setTargetId(''); }}
                  style={{ padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: mode === m.id ? '2px solid #3b82f6' : '1px solid #e5e7eb', background: mode === m.id ? '#dbeafe' : '#fff', color: mode === m.id ? '#1d4ed8' : '#4b5563', cursor: 'pointer', transition: 'all 0.15s' }}
                  data-testid={`mode-${m.id}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Promote — confirmation + sequence picker */}
          {mode === 'promote' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ padding: 12, borderRadius: 6, background: '#fefce8', border: '1px solid #fde68a', marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Promote to Module</div>
                <div style={{ fontSize: 11, color: '#78350f' }}>
                  "<strong>{node?.name}</strong>" will become a top-level Module. All child screens will move under the new Module.
                </div>
              </div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Insert after which Module? (sequence order)</label>
              <select value={afterModuleId} onChange={e => setAfterModuleId(e.target.value)} className="erp-input erp-select" style={{ fontSize: 12, width: '100%' }} data-testid="promote-after">
                <option value="">End of list (last)</option>
                {sortedModules.map(m => <option key={m.ModuleId} value={m.ModuleId}>{m.ModuleId} — {m.ModuleName}</option>)}
              </select>
            </div>
          )}

          {/* Target selector (for move / demote) */}
          {needsTarget && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                {mode === 'demote' ? 'Demote under Module:' : `Move to ${isSubmodule ? 'Module' : 'SubModule'}:`}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', background: '#dbeafe', padding: '4px 10px', borderRadius: 4, whiteSpace: 'nowrap' }}>{node?.name}</span>
                <ArrowRight size={14} color="#9ca3af" />
                <select value={targetId} onChange={e => setTargetId(e.target.value)} className="erp-input erp-select" style={{ fontSize: 12, flex: 1 }} data-testid="move-target">
                  <option value="">Select target...</option>
                  {targets.map(t => <option key={t.id} value={t.id}>{t.name} ({t.id})</option>)}
                </select>
              </div>
              {mode === 'demote' && (
                <div style={{ fontSize: 10, color: '#92400e', marginTop: 6, background: '#fefce8', padding: '4px 8px', borderRadius: 4 }}>
                  All child SubModules and Screens will move under the target Module.
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={onClose} className="erp-btn erp-btn-ghost"><X size={12} /> Cancel</button>
            <button onClick={handleExecute} disabled={saving || (needsTarget && !targetId)} className="erp-btn erp-btn-primary">
              <Move size={12} /> {saving ? 'Processing...' : mode === 'promote' ? 'Promote' : mode === 'demote' ? 'Demote' : 'Move'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   TREE NODE (memoized, with ellipsis action button)
═══════════════════════════════════════════════════ */
const TreeNode = memo(({ node, depth, expanded, onToggle, onSelect, selectedId, onContext }) => {
  const isExp = expanded.has(node.id);
  const isSelected = node.id === selectedId;
  const isLeaf = !node.hasChildren;
  const children = node._children || [];
  const indent = 12 + depth * 16;
  const colors = ['#1e40af', '#374151', '#4b5563'];
  const nodeColor = isSelected ? '#1d4ed8' : (colors[depth] || '#4b5563');
  const [hovered, setHovered] = useState(false);

  return (
    <div>
      <div
        onClick={() => { if (!isLeaf) onToggle(node.id); onSelect(node); }}
        onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onContext(e, node); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ padding: `4px 4px 4px ${indent}px`, fontSize: depth === 0 ? 13 : 12, fontWeight: depth === 0 ? 700 : (depth === 1 ? 600 : (isSelected ? 600 : 400)), cursor: 'pointer', color: nodeColor, display: 'flex', alignItems: 'center', gap: 4, background: isSelected ? '#dbeafe' : hovered ? '#f3f4f6' : 'transparent', borderRadius: 4, margin: '0 4px', transition: 'background 0.1s' }}
        data-testid={`tree-node-${node.id}`}
      >
        {!isLeaf && (isExp ? <ChevronDown size={depth === 0 ? 13 : 11} style={{ flexShrink: 0 }} /> : <ChevronRight size={depth === 0 ? 13 : 11} style={{ flexShrink: 0 }} />)}
        {isLeaf && <span style={{ width: 11, flexShrink: 0 }} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{node.name}</span>
        {node.childCount > 0 && <span style={{ fontSize: 9, color: '#9ca3af', flexShrink: 0 }}>({node.childCount})</span>}
        {/* Ellipsis action button — visible on hover */}
        <button
          onClick={e => { e.stopPropagation(); onContext(e, node); }}
          style={{ opacity: hovered || isSelected ? 1 : 0, flexShrink: 0, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer', color: '#6b7280', transition: 'opacity 0.15s, background 0.1s' }}
          onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = '#e5e7eb'; }}
          onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          data-testid={`tree-action-${node.id}`}
          title="Actions"
        >
          <MoreVertical size={14} />
        </button>
      </div>
      {isExp && children.map(child => (
        <TreeNode key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} onSelect={onSelect} selectedId={selectedId} onContext={onContext} />
      ))}
    </div>
  );
});

/* ═══════════════════════════════════════════════════
   MAIN SCREEN (inner, uses context)
═══════════════════════════════════════════════════ */
const ModuleManagementInner = ({ onBack }) => {
  const ctx = useModuleTree();
  const {
    modules, submodules, screens, dataLoading, loadAll,
    expanded, selectedNode, treeSearch, setTreeSearch, toggleNode, selectNode,
    page, setPage, pageSize, setPageSize, sortField, setSortField, sortDir, setSortDir, filters, setFilters,
    searchText, setSearchText, statusFilter, setStatusFilter, typeFilter, setTypeFilter,
    editNode, moveNode, deleteNode, resetFilters, moduleMap, submoduleMap, screenTypes,
  } = ctx;

  // Context menu
  const [ctxMenu, setCtxMenu] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [moveModal, setMoveModal] = useState(null);
  // Grid context menu
  const [gridCtx, setGridCtx] = useState(null);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Build tree
  const treeNodes = useMemo(() => {
    return modules
      .filter(m => !treeSearch || m.ModuleName?.toLowerCase().includes(treeSearch.toLowerCase()))
      .map(m => {
        const subs = submodules.filter(s => s.ParentModuleId === m.ModuleId);
        const subNodes = subs.map(s => {
          const scrns = screens.filter(sc => sc.SubModuleId === s.SubModuleId);
          return { id: `sub-${s.SubModuleId}`, name: s.SubModuleName, type: 'submodule', raw: s, hasChildren: scrns.length > 0, childCount: scrns.length,
            _children: scrns.map(sc => ({ id: `scr-${sc.ScreenId}`, name: sc.ScreenName, type: 'screen', raw: sc, hasChildren: false, childCount: 0, _children: [] })) };
        });
        return { id: `mod-${m.ModuleId}`, name: m.ModuleName, type: 'module', raw: m, hasChildren: subNodes.length > 0, childCount: subNodes.length, _children: subNodes };
      });
  }, [modules, submodules, screens, treeSearch]);

  const handleTreeContext = useCallback((e, node) => {
    const items = [
      { id: 'edit', label: 'Edit', icon: <Edit3 size={12} />, action: () => setEditModal(node) },
      { id: 'move', label: node.type === 'module' ? 'Demote / Move' : node.type === 'submodule' ? 'Move / Promote' : 'Move To...', icon: <Move size={12} />, action: () => setMoveModal(node) },
    ];
    items.push({ divider: true });
    items.push({ id: 'delete', label: 'Delete', icon: <Trash2 size={12} />, danger: true, action: async () => {
      if (window.confirm(`Delete "${node.name}"? This is a soft delete.`)) {
        const ok = await deleteNode(node);
        if (ok) erpToast.success('Deleted'); else erpToast.error('Delete failed');
      }
    }});
    setCtxMenu({ x: e.clientX, y: e.clientY, items });
  }, [deleteNode]);

  // Grid data
  const gridData = useMemo(() => {
    let items = screens;
    if (selectedNode) {
      if (selectedNode.type === 'module') items = items.filter(s => s.ModuleId === selectedNode.raw.ModuleId);
      else if (selectedNode.type === 'submodule') items = items.filter(s => s.SubModuleId === selectedNode.raw.SubModuleId);
      else if (selectedNode.type === 'screen') items = items.filter(s => s.ScreenId === selectedNode.raw.ScreenId);
    }
    if (searchText) items = items.filter(s => (s.ScreenName || '').toLowerCase().includes(searchText.toLowerCase()) || (s.ScreenId || '').toLowerCase().includes(searchText.toLowerCase()));
    if (statusFilter) items = items.filter(s => s.Status === statusFilter);
    if (typeFilter) items = items.filter(s => (s.ScreenType || '').trim() === typeFilter);
    if (sortField) items = [...items].sort((a, b) => { const av = String(a[sortField] || ''), bv = String(b[sortField] || ''); return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
    return items;
  }, [screens, selectedNode, searchText, statusFilter, typeFilter, sortField, sortDir]);

  const pageData = gridData.slice((page - 1) * pageSize, page * pageSize);
  const enrichedData = useMemo(() => pageData.map(s => ({
    ...s, ModuleName: moduleMap[s.ModuleId] || s.ModuleId, SubModuleName: submoduleMap[s.SubModuleId] || s.SubModuleId,
  })), [pageData, moduleMap, submoduleMap]);

  const kpi = useMemo(() => ({
    modules: modules.length, submodules: submodules.length, screens: screens.length,
    activeScreens: screens.filter(s => s.Status === 'S').length, filtered: gridData.length,
  }), [modules, submodules, screens, gridData]);

  const COLUMNS = [
    { field: 'ScreenId', header: 'Screen ID', width: 100 },
    { field: 'ScreenName', header: 'Screen Name', width: 220 },
    { field: 'ModuleName', header: 'Module', width: 160 },
    { field: 'SubModuleName', header: 'Sub Module', width: 160 },
    { field: 'ScreenType', header: 'Type', width: 100 },
    { field: 'Status', header: 'Status', width: 80 },
  ];

  const handleSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setPage(1); };

  // Grid row right-click
  const handleGridRowContext = useCallback((e, row) => {
    e.preventDefault();
    const screenNode = { id: `scr-${row.ScreenId}`, name: row.ScreenName, type: 'screen', raw: row };
    setGridCtx({ x: e.clientX, y: e.clientY, items: [
      { id: 'edit', label: 'Edit Screen', icon: <Edit3 size={12} />, action: () => setEditModal(screenNode) },
      { id: 'move', label: 'Move To...', icon: <Move size={12} />, action: () => setMoveModal(screenNode) },
      { divider: true },
      { id: 'delete', label: 'Delete', icon: <Trash2 size={12} />, danger: true, action: async () => {
        if (window.confirm(`Delete screen "${row.ScreenName}"?`)) {
          const ok = await deleteNode(screenNode);
          if (ok) erpToast.success('Deleted'); else erpToast.error('Failed');
        }
      }},
    ]});
  }, [deleteNode]);

  return (
    <FormHelp formName="ModuleManagement">
    <div style={{ display: 'flex', height: '100%' }} data-testid="module-management-screen" data-dev-component="ModuleManagement" data-dev-file="screens/system/ModuleManagementScreen.js" onContextMenu={e => { /* prevent default on grid rows handled per-row */ }}>
      {/* ── LEFT: Tree Panel ── */}
      <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid var(--erp-border)', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
        <div style={{ padding: '8px', borderBottom: '1px solid var(--erp-border)' }}>
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} />
            <input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Search modules..." className="erp-input" style={{ paddingLeft: 24, fontSize: 11, height: 28, width: '100%' }} data-testid="tree-search" />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }} data-testid="module-tree">
          {dataLoading ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}><Loader size={16} className="animate-spin" style={{ display: 'inline', marginRight: 6 }} />Loading...</div>
          ) : treeNodes.map(node => (
            <TreeNode key={node.id} node={node} depth={0} expanded={expanded} onToggle={toggleNode} onSelect={selectNode} selectedId={selectedNode?.id} onContext={handleTreeContext} />
          ))}
        </div>
        <div style={{ padding: '4px 8px', borderTop: '1px solid var(--erp-border)', fontSize: 9, color: '#9ca3af' }}>
          {modules.length} modules | {submodules.length} sub-modules | {screens.length} screens
        </div>
      </div>

      {/* ── RIGHT: Filters + KPI + Grid ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', background: 'var(--erp-surface)' }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--erp-primary)', marginRight: 'auto', minWidth: 100 }}>{selectedNode ? selectedNode.name : 'All Modules'}</span>
          <div style={{ position: 'relative' }}>
            <Search size={11} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} />
            <input value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }} placeholder="Search..." className="erp-input" style={{ paddingLeft: 22, height: 24, fontSize: 10, width: 140, borderRadius: 12 }} data-testid="grid-search" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 90, borderRadius: 12 }} data-testid="status-filter">
            <option value="">All Status</option><option value="S">Active</option><option value="D">Deleted</option>
          </select>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 110, borderRadius: 12 }} data-testid="type-filter">
            <option value="">All Types</option>{screenTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={resetFilters} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }} data-testid="reset-filters"><RotateCcw size={10} /> Reset</button>
        </div>

        {/* KPI */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-surface-alt)' }}>
          {[
            { label: 'Modules', value: kpi.modules, color: '#1e40af', icon: Layers },
            { label: 'SubModules', value: kpi.submodules, color: '#7c3aed', icon: Grid3X3 },
            { label: 'Screens', value: kpi.screens, color: '#0369a1', icon: Monitor },
            { label: 'Active', value: kpi.activeScreens, color: '#15803d', icon: Activity },
            { label: 'Filtered', value: kpi.filtered, color: '#b45309', icon: Filter },
          ].map(tile => (
            <div key={tile.label} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--erp-border)', background: 'var(--erp-surface)', minWidth: 0 }}>
              <div style={{ fontSize: 9, color: 'var(--erp-text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}><tile.icon size={10} />{tile.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: tile.color, marginTop: 2 }}>{tile.value}</div>
            </div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ERPAdvancedGrid
            title="" columns={COLUMNS} data={enrichedData} total={gridData.length}
            idField="ScreenId" nameField="ScreenName" page={page} pageSize={pageSize}
            onPageChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1); }}
            sortField={sortField} sortDir={sortDir} onSort={handleSort}
            filters={filters} onFilterChange={f => { setFilters(f); setPage(1); }}
            loading={dataLoading} showAddNew={false} testId="module-mgmt-grid"
            exportFileName="Module_Management" onBack={onBack}
            onRowClick={(row) => {
              // select in tree on grid row click
              const scrNode = { id: `scr-${row.ScreenId}`, name: row.ScreenName, type: 'screen', raw: row };
              selectNode(scrNode);
            }}
            cellRenderer={(field, value) => {
              if (field === 'Status') return <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: value === 'S' ? '#e8f8ef' : '#fdeceb', color: value === 'S' ? '#27ae60' : '#e74c3c' }}>{value === 'S' ? 'Active' : value}</span>;
              if (field === 'ScreenType') return <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: '#f0f4ff', color: '#3b5998' }}>{(value || '').trim()}</span>;
              return null;
            }}
            onEdit={(row) => {
              const scrNode = { id: `scr-${row.ScreenId}`, name: row.ScreenName, type: 'screen', raw: row };
              setEditModal(scrNode);
            }}
          />
        </div>
      </div>

      {/* Modals */}
      {ctxMenu && <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={() => setCtxMenu(null)} />}
      {gridCtx && <ContextMenu x={gridCtx.x} y={gridCtx.y} items={gridCtx.items} onClose={() => setGridCtx(null)} />}
      {editModal && <EditModal node={editModal} onClose={() => setEditModal(null)} onSave={editNode} />}
      {moveModal && <MoveModal node={moveModal} onClose={() => setMoveModal(null)} />}
    </div>
    </FormHelp>
  );
};

/* ═══════════════════════════════════════════════════
   WRAPPER — provides context
═══════════════════════════════════════════════════ */
const ModuleManagementScreen = ({ theme = 'light', onBack }) => (
  <ModuleTreeProvider>
    <ModuleManagementInner onBack={onBack} />
  </ModuleTreeProvider>
);

export default ModuleManagementScreen;
